import { useState } from 'react';
import {
  Body,
  Button,
  Dialog,
  DialogFooter,
  Field,
  Input,
  Row,
  Stack,
} from '@leanin/ui';
import { useTranslations } from '@/i18n/translations';
import { normalizeLinkUrl } from './link-url';

// Link entry dialog: RichTextEditor (toolbar Link button and Cmd/Ctrl+K) and
// a group's Resources library. Pure form UI: the caller applies the result —
// this component only collects text + URL and validates the URL against the
// http/https-only rule. Mounted per open (state initializes from props), so
// the caller renders it conditionally. Callers that save to the server pass
// `saving` / `error` and close the dialog themselves on success.

type Props = {
  /** Selection text (or the full existing link text) to prefill. */
  readonly initialText: string;
  /** Existing link href when editing; empty string when adding. */
  readonly initialUrl: string;
  /** True when opened on an existing link — titles the dialog "Edit link". */
  readonly isEditing: boolean;
  /** Called with the final display text and the normalized http/https URL. */
  readonly onSave: (text: string, url: string) => void;
  /** Shows the Remove action when editing. */
  readonly onRemove?: () => void;
  readonly onClose: () => void;
  /** Label for the text field; defaults to the editor's "Text". */
  readonly textLabel?: string;
  /** When true the text is required instead of falling back to the URL. */
  readonly requireText?: boolean;
  readonly saving?: boolean;
  readonly error?: string | null;
};

export function LinkDialog({
  initialText,
  initialUrl,
  isEditing,
  onSave,
  onRemove,
  onClose,
  textLabel,
  requireText = false,
  saving = false,
  error = null,
}: Props) {
  const t = useTranslations('Editor');
  const [text, setText] = useState(initialText);
  const [url, setUrl] = useState(initialUrl);
  const [textError, setTextError] = useState<string | null>(null);
  const [urlError, setUrlError] = useState<string | null>(null);

  const submit = () => {
    if (saving) return;
    const label = text.trim();
    const normalized = normalizeLinkUrl(url);
    if (requireText && label.length === 0) setTextError(t('linkTextRequired'));
    if (normalized === null) setUrlError(t('linkInvalid'));
    if (normalized === null || (requireText && label.length === 0)) return;
    onSave(label.length > 0 ? label : normalized, normalized);
  };

  const submitOnEnter = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    submit();
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      size="sm"
      title={isEditing ? t('linkDialogEditTitle') : t('linkDialogAddTitle')}
      // Land in the URL field, not the first-in-DOM text field: the display
      // text is usually prefilled from the selection, so the URL is the one
      // thing the member came here to type. Selected on edit so an existing
      // href replaces in one keystroke.
      onOpenAutoFocus={(event) => {
        event.preventDefault();
        const content = event.currentTarget as HTMLElement | null;
        const urlInput = content?.querySelector<HTMLInputElement>('input[inputmode="url"]');
        urlInput?.focus();
        urlInput?.select();
      }}
    >
      <Stack gap="md">
        <Field label={textLabel ?? t('linkTextLabel')} error={textError} required={requireText}>
          <Input
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setTextError(null);
            }}
            onKeyDown={submitOnEnter}
          />
        </Field>
        <Field label={t('linkUrlLabel')} error={urlError} required>
          <Input
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              setUrlError(null);
            }}
            onKeyDown={submitOnEnter}
            placeholder={t('linkUrlPlaceholder')}
            inputMode="url"
          />
        </Field>
        {error !== null ? (
          <Body size="sm" color="destructive">{error}</Body>
        ) : null}
      </Stack>
      <DialogFooter justify={isEditing && onRemove ? 'space-between' : 'flex-end'}>
        {isEditing && onRemove ? (
          <Button variant="ghostDanger" onClick={onRemove}>
            {t('linkRemove')}
          </Button>
        ) : null}
        <Row gap="sm" align="center">
          <Button variant="ghost" onClick={onClose}>
            {t('linkCancel')}
          </Button>
          <Button variant="primary" onClick={submit} loading={saving}>
            {t('linkSave')}
          </Button>
        </Row>
      </DialogFooter>
    </Dialog>
  );
}
